/**
 * Replaces a native <select multiple> with a custom div-based listbox
 * that matches the design's offer list style (amber checkbox indicator,
 * city shown on the right, hover/selected states).
 *
 * The original <select> is kept in the DOM (hidden) because:
 *   - offer-groups.ts reads selectedOptions from it
 *   - offer-groups.ts and search-filter.ts mutate its innerHTML
 *   - MutationObserver syncs the custom listbox when the <select> changes
 */

const CHECK_SVG =
    '<svg width="10" height="10" viewBox="0 0 10 10" fill="none">' +
    '<path d="M1.5 5L4 7.5 8.5 2" stroke="white" stroke-width="1.8" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

export function setupOfferListbox(selectEl: HTMLSelectElement): void {
    // Create and inject the custom listbox immediately before the <select>
    const listbox = document.createElement('div');
    listbox.className = 'cc-offer-listbox';
    selectEl.insertAdjacentElement('beforebegin', listbox);

    // Hide native select (kept in DOM for state management)
    selectEl.style.display = 'none';

    let rendering = false;

    function render(): void {
        if (rendering) return;
        rendering = true;

        listbox.innerHTML = '';
        const options = Array.from(selectEl.options);

        if (options.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'cc-offer-listbox-empty';
            empty.textContent = '—';
            listbox.appendChild(empty);
            rendering = false;
            return;
        }

        for (const opt of options) {
            const item = document.createElement('div');
            item.className = 'cc-offer-listbox-item' + (opt.selected ? ' selected' : '');
            item.dataset.value = opt.value;

            // Visual checkbox
            const cbBox = document.createElement('div');
            cbBox.className = 'cc-offer-lb-cb';
            if (opt.selected) cbBox.innerHTML = CHECK_SVG;

            // Offer name
            const nameSpan = document.createElement('span');
            nameSpan.className = 'cc-offer-lb-text';
            nameSpan.textContent = opt.textContent?.trim() || '';

            // City (right-aligned muted text)
            const city = opt.dataset.city || '';
            const citySpan = document.createElement('span');
            citySpan.className = 'cc-offer-lb-city';
            citySpan.textContent = city;

            item.append(cbBox, nameSpan, citySpan);

            item.addEventListener('click', () => {
                // Toggle selection on the underlying <select> option
                opt.selected = !opt.selected;
                // Sync visual state immediately (before MutationObserver fires)
                item.classList.toggle('selected', opt.selected);
                cbBox.innerHTML = opt.selected ? CHECK_SVG : '';
                // Dispatch change so offer-groups.ts listeners fire
                selectEl.dispatchEvent(new Event('change', { bubbles: true }));
            });

            listbox.appendChild(item);
        }

        rendering = false;
    }

    // Re-render whenever offer-groups.ts or search-filter.ts mutates the <select>
    const observer = new MutationObserver(() => {
        // Debounce slightly so a full innerHTML replace fires only once
        requestAnimationFrame(render);
    });
    observer.observe(selectEl, { childList: true, subtree: false });

    // Initial render
    render();
}

/**
 * Sets up custom listboxes for all three offer-group <select> elements.
 */
export function setupOfferListboxes(
    accommodationSelect: HTMLSelectElement,
    transportSelect: HTMLSelectElement,
    insuranceSelect: HTMLSelectElement,
): void {
    setupOfferListbox(accommodationSelect);
    setupOfferListbox(transportSelect);
    setupOfferListbox(insuranceSelect);
}
